import {
  LabelSelectNoOptions_custom,
  LabelSelectOption_custom,
  LabelSelectSingleValue_custom,
  labelSelectStyles_custom,
  PlaceholderOptions_custom,
  UserSelectDropDownIndicator,
} from '@sketchnote/shared-components';
import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { useBoardStore } from 'src/features/boards';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { v4 as uuid } from 'uuid';

import { useTasksStore } from '../../store/useTasksStore';
import { Task } from '../../types/tasks';

const randomColor = [
  '#fbb6ce',
  '#d6bcfa',
  '#9BADEE',
  '#81e6d9',
  '#86E495',
  '#FFE98F',
  '#fbd38d',
  '#FBB1A7',
];
export const SelectFieldComponent = ({
  task,
  fieldId,
  data,
  disabled,
}: {
  task: Partial<Task>;
  fieldId: string;
  label: string;
  data: string;
  handleDelete: (flag: boolean) => void;
  disabled?: boolean;
}): JSX.Element => {
  const [selectedOption, setSelectedOption] = useState(data);
  const [isFocused, setIsFocused] = useState(false);

  const boardsStore = useBoardStore();

  const fetchSelectables = useBoardStore((s) => s.actions.fetchSelectables);
  const { selectables } = useBoardStore((s) => ({ selectables: s.selectables }));

  const onUpdateCustomFieldData = useTasksStore((s) => s.onUpdateCustomFieldData);
  const onUpdateCustomTemplateFieldData = useTasksStore((s) => s.onUpdateCustomTemplateFieldData);

  const { isReadOnly } = useTaskAccess(task.id || '');

  const router = useRouter();

  useEffect(() => {
    setSelectedOption(data);
  }, [data]);

  useEffect(() => {
    if (task.board?.id === undefined) {
      fetchSelectables({ id: task.id as string, isTaskTemplateId: true });
    } else {
      fetchSelectables({ id: router?.query?.view as string });
    }
  }, [selectedOption]);

  const handleChange = async (value) => {
    if (value) {
      const updatedLabel = value.color
        ? value
        : {
            label: value.label,
            value: value.value,
            color: randomColor[Math.floor(Math.random() * randomColor.length)],
            id: uuid(),
          };
      // Add the created option to the task store
      if (task.id) {
        setSelectedOption(updatedLabel);
        const payload = { taskId: task.id, fieldId: fieldId, data: [updatedLabel] };
        if (task.board?.id === undefined) {
          payload['taskTemplateRef'] = task.id;
          onUpdateCustomTemplateFieldData(payload);
        } else {
          payload['taskTemplateRef'] = undefined;
          payload['cb'] = () => fetchSelectables({ id: router?.query?.view as string });
          await onUpdateCustomFieldData(payload);
        }
      }
    } else {
      setSelectedOption(value);
    }
  };

  return (
    <CreatableSelect
      isClearable
      isDisabled={disabled || isReadOnly}
      className={cn('text-sm text-gray-999 font-regular placeholder-gray-500 h-36 w-400', {
        'bg-white rounded-sm border-gray-300': isFocused,
      })}
      menuPlacement="auto"
      options={selectables[fieldId]?.length > 0 ? selectables[fieldId] : []}
      menuPortalTarget={document.body}
      onChange={handleChange}
      value={selectedOption}
      components={{
        DropdownIndicator: isReadOnly ? null : UserSelectDropDownIndicator,
        IndicatorSeparator: () => null,
        NoOptionsMessage: LabelSelectNoOptions_custom,
        Placeholder: PlaceholderOptions_custom,
        SingleValue: LabelSelectSingleValue_custom,
        Option: LabelSelectOption_custom,
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      styles={labelSelectStyles_custom}
    />
  );
};
